import React from 'react'
import {Box, BoxProps, HStack, useMultiStyleConfig, Text, Popover, PopoverTrigger, PopoverContent, useDisclosure} from '@chakra-ui/react'
import {Button, ColorCircle, Icon, Price} from '_uiComponents'
import {FullProductType, ProductVariant, ProductVariationGroup} from '_types'
import ProductSizeSelector from '../size-selector'
import ProductColorSelector from '../color-selector'
import {useAnalytics} from '_customHooks'
import {useIntl} from 'react-intl'

interface StickybarProps {
    product: FullProductType
    // Price values mirror the ones fed to the PDP page <Price> so the sticky bar updates
    // identically (color/size changes, discounts and kids "starting from" ranges).
    priceValue?: string
    discountedPrice?: string
    discountedPriceMinRange?: string
    priceMinRange?: string
    activeVariation: ProductVariationGroup
    selectedVariant?: ProductVariant
    shouldAddToCartAfterSizeSelection: React.MutableRefObject<boolean>
    onAddToCart: () => void
    onSizeSelected: (sizeCode: string, variant: ProductVariant) => void
    onColorSelected: (colorCode: string, variationGroup: ProductVariationGroup) => void
    onRequestSize: (sizeCode: string, variant: ProductVariant) => void
}

/**
 * @name Stickybar
 *
 * @component
 *
 * @description
 * Renders a menu and a dark overlay.
 *
 * @remarks
 * Dark Overlay component ensures that on tablet the correct background under the sidebar is rendered.
 *
 * @returns {React.FC} - The rendered sidebar component.
 */
const Stickybar: React.FC<StickybarProps> = ({
    product,
    priceValue,
    discountedPrice,
    discountedPriceMinRange,
    priceMinRange,
    activeVariation,
    selectedVariant,
    onAddToCart,
    onSizeSelected,
    onColorSelected,
    onRequestSize,
    shouldAddToCartAfterSizeSelection,
}: StickybarProps) => {
    const {isOpen: isSizePopoverOpen, onToggle: onSizePopoverToggle, onClose: onSizePopoverClose} = useDisclosure()
    const {isOpen: isColourPopoverOpen, onToggle: onColourPopoverToggle, onClose: onColourPopoverClose} = useDisclosure()
    const {analyticsProductSizeSelectorOpenClose, analyticsProductSizeSelectorSizeSelect, analyticsEventPdpColorLayer, analyticsEventPdpColorLayerSelect} =
        useAnalytics()
    const {formatMessage} = useIntl()

    const LABEL_ADD_TO_CART = formatMessage({id: 'button.addtocart', defaultMessage: 'ADD TO CART'})?.toUpperCase()
    const LABEL_SIZE = formatMessage({id: 'size.button.tile', defaultMessage: 'Size'})?.toLowerCase()
    const LABEL_COLOUR = formatMessage({id: 'label.pdp.color', defaultMessage: 'Colour'})?.toLowerCase()

    const handleAddToCart = () => {
        if (!selectedVariant && !shouldAddToCartAfterSizeSelection.current) {
            shouldAddToCartAfterSizeSelection.current = true
            onSizePopoverToggle()
        } else {
            onAddToCart()
        }
    }

    const handleOpenColourSelectorPopover = () => {
        // Manuale BitBang PDP PWA §3.1.4 — color layer opened from the stickybar.
        analyticsEventPdpColorLayer({action: 'open'})
    }

    const handleCloseColourSelectorPopover = () => {
        onColourPopoverClose()

        if (shouldAddToCartAfterSizeSelection.current) {
            shouldAddToCartAfterSizeSelection.current = false
        }
        analyticsEventPdpColorLayer({action: 'close'})
    }

    const handleOpenSizeSelectorPopover = () => {
        analyticsProductSizeSelectorOpenClose({action: 'open', productId: activeVariation?.ID, fromStickybar: true})
    }

    const handleCloseSizeSelectorPopover = (hasSelectedSize?: boolean) => {
        onSizePopoverClose()

        // disable add to cart on size selection if popover was closed
        if (!hasSelectedSize && shouldAddToCartAfterSizeSelection.current) {
            shouldAddToCartAfterSizeSelection.current = false
        }

        // SFRA spec §6.4: 'close' is emitted only when the size menu is dismissed WITHOUT
        // selecting a size. A size selection emits only 'size select'.
        if (!hasSelectedSize) {
            analyticsProductSizeSelectorOpenClose({action: 'close', productId: activeVariation?.ID, fromStickybar: true})
        }
    }

    const styles = useMultiStyleConfig('ProductStickybar', {}) as Record<string, BoxProps>

    return (
        <HStack {...styles.stickybarWrapper} data-testid="pdp-stickybar-wrapper">
            <Text {...styles.stickyName}>{product.c_shortDescription}</Text>
            <Price
                variant="stickybar"
                discountedPrice={discountedPrice}
                discountedPriceMinRange={discountedPriceMinRange}
                priceMinRange={priceMinRange}
            >
                {priceValue}
            </Price>
            <Box {...styles.stickyColorSelector}>
                <Popover isOpen={isColourPopoverOpen} onClose={handleCloseColourSelectorPopover} onOpen={handleOpenColourSelectorPopover}>
                    <PopoverTrigger>
                        <Button variant="text" onClick={onColourPopoverToggle}>
                            <Box {...styles.swatchItem} aria-hidden="true">
                                <ColorCircle hexCodes={activeVariation?.color?.colorHexCode ?? null} selected />
                            </Box>
                            <Text {...styles.stickyColourButton}>{activeVariation?.color?.label || LABEL_COLOUR}</Text>
                            {isColourPopoverOpen ? <Icon name="IconChevronUp" size="sm" /> : <Icon name="IconChevronDown" size="sm" />}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent sx={styles.colourSelectorPopover}>
                        <Box>
                            <ProductColorSelector
                                variationGroups={product.c_variationGroupsData}
                                selectedColorCode={activeVariation?.color?.code}
                                onColorSelected={(colorCode, variationGroup) => {
                                    // 'select color'/'click' when changing color from the stickybar color layer
                                    // (Manuale BitBang PDP PWA §3.1.4), before closing so it precedes 'close'.
                                    analyticsEventPdpColorLayerSelect()
                                    onColorSelected(colorCode, variationGroup)
                                    // Use the close handler (not the bare disclosure onClose) so the layer
                                    // 'close' analytics event is emitted on selection-driven close too.
                                    handleCloseColourSelectorPopover()
                                }}
                            />
                        </Box>
                    </PopoverContent>
                </Popover>
            </Box>
            <Box {...styles.stickySizeSelector}>
                <Popover isOpen={isSizePopoverOpen} onClose={handleCloseSizeSelectorPopover} onOpen={handleOpenSizeSelectorPopover}>
                    <PopoverTrigger>
                        <Button variant="text" onClick={onSizePopoverToggle}>
                            <Text {...styles.stickySizeButton}>{selectedVariant?.size?.label || LABEL_SIZE}</Text>
                            {isSizePopoverOpen ? <Icon name="IconChevronUp" size="sm" /> : <Icon name="IconChevronDown" size="sm" />}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent sx={styles.sizeSelectorPopover}>
                        <Box>
                            <ProductSizeSelector
                                variants={activeVariation?.variants}
                                onSizeSelected={(sizeCode, variant) => {
                                    onSizeSelected(sizeCode, variant)

                                    analyticsProductSizeSelectorSizeSelect({productId: activeVariation?.ID, selectedSizeId: sizeCode, fromStickybar: true})

                                    if (shouldAddToCartAfterSizeSelection.current) {
                                        handleAddToCart()
                                    }

                                    handleCloseSizeSelectorPopover(true)
                                }}
                                notifyMeEnabled={activeVariation.notifyMeEnabled}
                                onRequestSize={(sizeCode, variant) => onRequestSize(sizeCode, variant)}
                            />
                        </Box>
                    </PopoverContent>
                </Popover>
            </Box>
            <Box {...styles.stickyAddToCartWrapper}>
                <Button variant="primary" sx={styles.stickyAddToCartButton} onClick={handleAddToCart}>
                    {LABEL_ADD_TO_CART}
                </Button>
            </Box>
        </HStack>
    )
}

Stickybar.displayName = 'Stickybar'

export default Stickybar
